import {
  ClassicEditor as ClassicEditorBase, Essentials, Autoformat, Bold, Italic, Underline,
  Strikethrough, BlockQuote, Heading, Indent, Link, List, Paragraph, PasteFromOffice,
  Table, TableToolbar, Font, Plugin, ButtonView, InputNumberView,
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import './RichEditor.css';

// Inline text formats copied/pasted by the painter.
const PAINT_ATTRS = [
  'bold', 'italic', 'underline', 'strikethrough',
  'fontSize', 'fontFamily', 'fontColor', 'fontBackgroundColor',
];

const PAINT_ICON =
  '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M4 2h10a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zm0 1.5v2h10v-2H4zM8 8h2v2h3a1 1 0 0 1 1 1v1H6v-1a1 1 0 0 1 1-1h1V8zm-1 6h4v4a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1v-4z"/></svg>';

// Click once to copy the format at the cursor, then select text to apply it.
class FormatPainter extends Plugin {
  init() {
    const editor = this.editor;
    let copied = null;

    editor.ui.componentFactory.add('formatPainter', (locale) => {
      const btn = new ButtonView(locale);
      btn.set({ label: 'Format painter', icon: PAINT_ICON, tooltip: true });
      btn.bind('isOn').to(this, 'active');
      btn.on('execute', () => {
        if (copied) {
          copied = null;
          this.active = false;
          return;
        }
        const sel = editor.model.document.selection;
        copied = {};
        PAINT_ATTRS.forEach((a) => (copied[a] = sel.getAttribute(a)));
        this.active = true;
      });
      return btn;
    });
    this.set('active', false);

    editor.editing.view.document.on('mouseup', () => {
      if (!copied) return;
      const { model } = editor;
      const sel = model.document.selection;
      if (sel.isCollapsed) return;
      model.change((writer) => {
        for (const range of sel.getRanges()) {
          PAINT_ATTRS.forEach((a) =>
            copied[a] === undefined
              ? writer.removeAttribute(a, range)
              : writer.setAttribute(a, copied[a], range),
          );
        }
      });
      copied = null;
      this.active = false;
    });
  }
}

// Grow/shrink buttons step by 1px; 18 = default (no attribute).
const ICON = (d) => `<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><text x="1" y="15" font-size="13" font-weight="bold" font-family="Arial">A</text><path d="${d}"/></svg>`;

// Normalise any stored size (px, or pt from pasted Word content) to px; no attribute = default.
const DEFAULT = 18;
// CKEditor named presets (from ckeditor5 font CSS).
const NAMED = { tiny: 0.7, small: 0.85, big: 1.4, huge: 1.8 };
const toPx = (v) => {
  if (!v) return DEFAULT;
  if (NAMED[v]) return Math.round(NAMED[v] * DEFAULT);
  const n = parseFloat(v);
  if (!(n > 0)) return DEFAULT;
  if (/pt$/i.test(v)) return Math.round((n * 4) / 3);
  if (/r?em$/i.test(v)) return Math.round(n * DEFAULT);
  if (/%$/.test(v)) return Math.round((n / 100) * DEFAULT);
  return Math.round(n); // px or bare number
};
// Size of one text run, read from the model (never the DOM, which lags during edits).
const sizeOf = (node) => toPx(node.getAttribute('fontSize'));

class FontSizeStep extends Plugin {
  // Apply fn(currentPx) -> newPx to every text run in the selection, each from its OWN size.
  resize(fn) {
    const { model } = this.editor;
    const sel = model.document.selection;
    model.change((writer) => {
      if (sel.isCollapsed) {
        const px = fn(sizeOf(sel));
        if (px === DEFAULT) writer.removeSelectionAttribute('fontSize');
        else writer.setSelectionAttribute('fontSize', `${px}px`);
        return;
      }
      for (const range of sel.getRanges()) {
        for (const item of [...range.getItems()]) {
          if (!item.is('$textProxy')) continue;
          const r = writer.createRangeOn(item);
          const px = fn(sizeOf(item));
          if (px === DEFAULT) writer.removeAttribute('fontSize', r);
          else writer.setAttribute('fontSize', `${px}px`, r);
        }
      }
    });
    this.editor.editing.view.focus();
  }

  // Distinct sizes in the selection: one number, or '-' if mixed.
  currentLabel() {
    const sel = this.editor.model.document.selection;
    if (sel.isCollapsed) return `${sizeOf(sel)}`;
    const sizes = new Set();
    for (const range of sel.getRanges())
      for (const item of range.getItems())
        if (item.is('$textProxy')) sizes.add(sizeOf(item));
    return sizes.size === 1 ? `${[...sizes][0]}` : sizes.size ? '-' : `${DEFAULT}`;
  }

  init() {
    const editor = this.editor;
    const step = (dir) => (cur) => Math.min(200, Math.max(1, Math.round(cur) + dir));
    const add = (name, label, d, dir) =>
      editor.ui.componentFactory.add(name, (locale) => {
        const btn = new ButtonView(locale);
        btn.set({ label, icon: ICON(d), tooltip: true });
        btn.on('execute', () => this.resize(step(dir)));
        return btn;
      });
    // Editable size box: type a size (1-200) and press Enter (or leave the box) to apply.
    editor.ui.componentFactory.add('fontSizeLabel', (locale) => {
      const input = new InputNumberView(locale, { min: 1, max: 200, step: 1 });
      input.extendTemplate({ attributes: { class: 'ck-font-size-input', title: 'Font size' } });
      const show = () => {
        const l = this.currentLabel();
        input.value = l === '-' ? '' : l;
      };
      show();
      editor.model.document.selection.on('change:range', show);
      editor.model.document.on('change:data', show);
      const apply = () => {
        const v = Math.round(parseFloat(input.element.value));
        if (v >= 1 && v <= 200) this.resize(() => v);
        else show();
      };
      input.on('render', () => {
        input.element.addEventListener('keydown', (e) => {
          e.stopPropagation(); // keep arrows/backspace inside the box, not the toolbar
          if (e.key === 'Enter') { e.preventDefault(); apply(); }
        });
        input.element.addEventListener('change', apply);
      });
      return input;
    });
    add('fontSizeUp', 'Increase font size', 'M14 3l3 4h-6z', 1);
    add('fontSizeDown', 'Decrease font size', 'M14 7l3-4h-6z', -1);
  }
}

export default class RichEditor extends ClassicEditorBase {}

RichEditor.builtinPlugins = [
  Essentials, Autoformat, Bold, Italic, Underline, Strikethrough, BlockQuote, Heading,
  Indent, Link, List, Paragraph, PasteFromOffice, Table, TableToolbar, Font, FormatPainter, FontSizeStep,
];

RichEditor.defaultConfig = {
  toolbar: [
    'heading', '|', 'fontSize', 'fontSizeDown', 'fontSizeLabel', 'fontSizeUp', 'fontFamily', 'fontColor', 'fontBackgroundColor', '|',
    'bold', 'italic', 'underline', 'strikethrough', 'formatPainter', '|',
    'link', 'bulletedList', 'numberedList', 'outdent', 'indent', '|',
    'blockQuote', 'insertTable', 'undo', 'redo',
  ],
  // unsized text renders at 18px in editor and PDF; unsized text is 18px via CSS
  fontSize: { options: [9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32], supportAllValues: true },
  table: { contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'] },
  language: 'en',
  licenseKey: 'GPL',
};
